/*
 * Consent gate — Widerruf waiver for digital products (SKU starting "E1500").
 * -------------------------------------------------------------------------
 * The checkbox + text are rendered server-side (cart page + cart drawer) only
 * when the cart contains an E1500* SKU, and the checkout button is rendered
 * `disabled` on first paint. This script:
 *   - enables the checkout button(s) the moment the box is ticked (and
 *     re-disables if unticked),
 *   - re-applies the state after the drawer opens or the cart re-renders,
 *   - and, as a backstop, blocks the checkout click/submit while the box is
 *     unchecked — even if a third-party app clones or re-enables the button.
 *
 * It gates by the `disabled` property (not `pointer-events`), so it does NOT
 * fight assets/checkout-recovery.js, which only touches `pointer-events`.
 *
 * Scope note: express/dynamic checkout buttons (Shop Pay, PayPal, Apple/Google
 * Pay) are a separate element and are NOT gated here — only the "Zur Kasse"
 * button is, as specified.
 */
(function () {
  'use strict';

  var CHECKOUT_SEL = 'button[name="checkout"], .cart__submit, .Cart__Checkout.btn--primary';
  var BOX_SEL = '[data-consent-checkbox]';

  function isCheckoutButton(el) {
    return !!el && !!el.matches && el.matches(CHECKOUT_SEL) && !el.matches('[data-action="close-drawer"]');
  }

  // The consent checkbox governing a given checkout button is the one in the
  // same cart <form>. Returns null when this form has no gate (no E1500 item).
  function boxForButton(btn) {
    var form = btn.closest('form');
    return form ? form.querySelector(BOX_SEL) : null;
  }

  function syncButton(btn) {
    if (!isCheckoutButton(btn)) return;
    var box = boxForButton(btn);
    if (!box) return;
    var blocked = !box.checked;
    // Gate for a11y + the theme's greying via aria-disabled (core.css greys
    // .btn[aria-disabled]). We deliberately do NOT use the native `disabled`
    // attribute: a disabled button swallows the click, so we could never show
    // the confirmation dialog. The capture-phase handlers below do the actual
    // blocking, and consent-gate.css keeps the button clickable.
    if (blocked) {
      btn.setAttribute('aria-disabled', 'true');
    } else {
      btn.removeAttribute('aria-disabled');
    }
    btn.classList.toggle('consent-blocked', blocked);
  }

  function syncAll() {
    var btns = document.querySelectorAll(CHECKOUT_SEL);
    for (var i = 0; i < btns.length; i++) syncButton(btns[i]);
  }

  function flash(box) {
    var gate = box.closest('[data-consent-gate]') || box;
    try { gate.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (e) {}
    gate.classList.remove('consent-gate--flash');
    void gate.offsetWidth; // restart the CSS animation
    gate.classList.add('consent-gate--flash');
  }

  // Dialog shown when the customer clicks checkout before confirming.
  var DIALOG_TEXT =
    'Bitte bestätige zuerst die Erklärung bezüglich der digitalen Produkte, um zur Kasse gehen zu können';
  var dialogEl = null;

  function buildDialog() {
    var overlay = document.createElement('div');
    overlay.className = 'consent-dialog-overlay';
    overlay.innerHTML =
      '<div class="consent-dialog" role="alertdialog" aria-modal="true" aria-describedby="consent-dialog-msg" tabindex="-1">' +
        '<p id="consent-dialog-msg" class="consent-dialog__msg"></p>' +
        '<button type="button" class="btn btn--primary consent-dialog__close">OK</button>' +
      '</div>';
    overlay.querySelector('.consent-dialog__msg').textContent = DIALOG_TEXT;

    function close() { overlay.classList.remove('is-open'); }
    // Stop clicks inside the dialog from reaching the theme's document-level
    // "click outside the cart closes the drawer" handler, so the slide-in cart
    // stays open when the dialog is dismissed (OK, backdrop, or anywhere else).
    overlay.addEventListener('click', function (e) {
      e.stopPropagation();
      if (e.target === overlay) close();
    });
    overlay.querySelector('.consent-dialog__close').addEventListener('click', function (e) {
      e.stopPropagation();
      close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) close();
    });

    document.body.appendChild(overlay);
    return overlay;
  }

  function showDialog() {
    if (!dialogEl) dialogEl = buildDialog();
    dialogEl.classList.add('is-open');
    var ok = dialogEl.querySelector('.consent-dialog__close');
    if (ok) { try { ok.focus(); } catch (e) {} }
  }

  function warn(box) {
    flash(box);
    showDialog();
  }

  // Live enable/disable as the box is toggled.
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || !t.matches || !t.matches(BOX_SEL)) return;
    var form = t.closest('form');
    if (!form) return;
    var btns = form.querySelectorAll(CHECKOUT_SEL);
    for (var i = 0; i < btns.length; i++) syncButton(btns[i]);
  });

  // Backstop: block the checkout click before any app handler runs.
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest(CHECKOUT_SEL) : null;
    if (!isCheckoutButton(btn)) return;
    var box = boxForButton(btn);
    if (box && !box.checked) {
      e.preventDefault();
      e.stopImmediatePropagation();
      warn(box);
    }
  }, true);

  // Backstop for a native cart-form submit.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.querySelector) return;
    var box = form.querySelector(BOX_SEL);
    if (box && !box.checked) {
      e.preventDefault();
      e.stopImmediatePropagation();
      warn(box);
    }
  }, true);

  // Re-apply after the drawer opens or the cart re-renders (scoped observers,
  // childList only — our own disabled/class changes are attributes, so they
  // don't retrigger these).
  function watch(el) {
    if (!el || typeof MutationObserver === 'undefined') return;
    var timer;
    new MutationObserver(function () {
      clearTimeout(timer);
      timer = setTimeout(syncAll, 50);
    }).observe(el, { childList: true, subtree: true });
  }

  function init() {
    syncAll();
    watch(document.getElementById('sidebar-cart'));
    var cartPage = document.querySelector('[data-section-type="cart-template"]');
    if (cartPage) watch(cartPage);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('pageshow', syncAll);
})();
